import { HttpEventType } from "@angular/common/http";
import {
  Component,
  Input,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  OnInit,
  ElementRef,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MetadataSet } from "../../../insa-models";
import {
  AuthService,
  InsaMetadataService,
  InsaService,
  LocalizeService,
} from "../../../insa-services";
import { GenericEditorEventProvider } from "./GenericEditorStateProvider";
import { CommonField, PersistedFieldOption } from "./models/Field";
import {
  FieldGroupDefinition,
  makeFieldGroup,
  makeRenderableFieldGroup,
  RenderableFieldGroup,
} from "./models/FieldGroup/FieldGroup";
import {
  convertFormToDTO,
  customFieldsToFormGroup,
  InsaFieldsToCustomFields,
} from "./utilities";
import makeParsedMetadataGroups, {
  makeSortedFields,
} from "./models/FieldGroup/parsedMetadataGroups";
import computeKeyFields from "../utils/keyFields";
import { makeParsedMetadataSet } from "../utils/parsedMetadataSet";
import { GridStateService } from "../../../insa-services/gridState.service";
import { data } from "jquery";
import { SecPricesExtendedModel } from "../../../insa-models";
import { GenericChartsModule } from "../../../insa-custom-scripts";
import { appStateProvider } from "../../../insa-providers";
import { createChart, IChartApi, ColorType } from "lightweight-charts";
import { ChangeDetectorRef } from "@angular/core";

@Component({
  selector: "generic-editor",
  styles: [require("./generic-editor.component.css").toString()],
  templateUrl: "generic-editor.component.html",
})
export class GenericEditorComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
  @Input() metadataName?: string; // either this or `fielGroups` is required
  @Input() selectedId: any;
  @Input() fieldGroups?: FieldGroupDefinition[]; // either this or `metadataName` is required
  @Input() onBack: () => void;
  @Input() onAfterSubmitSuccess?: () => void;
  @Input() canDelete: boolean = true;
  @Input() canEdit: boolean = true;

  fields?: CommonField[];
  metadataSet: MetadataSet;
  renderableFieldGroups?: RenderableFieldGroup[];
  title: string = "Form title";
  form?: FormGroup;
  submitLabel = "Submit";
  metaDataSubscription;
  fileContentSubscribers = [];
  keyFields: string[];
  alternativeKeyField?: string;
  isEditorWizardVisible: boolean = false;
  isEditorWizardBtnVisible: boolean = false;
  _maxId: number;
  gridSelector: any = {
    isVisible: false,
    metadataName: null,
  };
  onGridItemSelect = this.gridSelectorSelectHandler.bind(this);
  entityDetails = {
    isVisible: false,
    metadataName: null,
    id: null,
    formOptions: {
      onBack: () => (this.entityDetails.isVisible = false),
    },
  };
  securityDropdown: kendo.ui.DropDownList;

  private secPricesSubscription;
  private chart: IChartApi;

  constructor(
    private _appStateProvider: appStateProvider,
    private _insaMetadataService: InsaMetadataService,
    public eventProvider: GenericEditorEventProvider,
    private _localize: LocalizeService,
    private _authService: AuthService,
    private _gridStateService: GridStateService,
    private _insaService: InsaService,
    private _element: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
    if (_authService.UserInfo) {
      this.isEditorWizardBtnVisible =
        _authService.UserInfo.Roles.includes("InsaUser");
    } else {
      _authService.UserInfoEmitter.subscribe((userInfo) => {
        this.isEditorWizardBtnVisible = userInfo.Roles.includes("InsaUser");
      });
    }
    this.secPricesSubscription =
      this._insaService.SecurityPricesChartEvent.subscribe((secPrices) =>
        this.refreshSecPricesChart(secPrices),
      );
  }

  private refreshSecPricesChart(secPrices: SecPricesExtendedModel) {
    if (
      secPrices !== undefined &&
      secPrices.SecurityPricesCollection.length > 0
    ) {
      GenericChartsModule.InitKendoGenericMultipaneStockChart(
        "#GenericSecurityPrices",
        secPrices.SecurityPricesCollection,
        "area",
        "column",
        "Price",
        "SECURITY PRICES",
        true,
        "Date",
        true,
        secPrices.MinDate,
        secPrices.MaxDate,
        "Volume",
        secPrices.MinVolume,
        secPrices.MaxVolume,
        $(".canvas").height() - $("#customButtonRow").height(),
        $(".canvas").width(),
        this._localize.getLocalizedResource("Res711"),
      );
      GenericChartsModule.KendoInitTimeIntervalPickerForSecPrices(
        "#GenericSecurityPrices",
        secPrices.MinDate,
        secPrices.MaxDate,
      );
      $("#securityMessage").html("");
    } else {
      GenericChartsModule.InitKendoGenericMultipaneStockChart(
        "#GenericSecurityPrices",
        [],
        "area",
        "column",
        "Price",
        "SECURITY PRICES",
        false,
        "Date",
        false,
        null,
        null,
        "Volume",
        null,
        null,
        undefined,
        undefined,
        this._localize.getLocalizedResource("Res711"),
      );
      GenericChartsModule.KendoInitTimeIntervalPickerForSecPrices(
        "#GenericSecurityPrices",
        null,
        null,
      );
      kendo.ui.progress($(".stockChart-loading"), false);
      $("#securityMessage").html(
        '<p class="center no-data-message">NO DATA</p>',
      );
    }
  }

  private onResize(event) {
    let secPricesChart: kendo.dataviz.ui.StockChart = $(
      "#GenericSecurityPrices",
    ).data("kendoStockChart");
    if (secPricesChart !== undefined) {
      secPricesChart.options.chartArea = {
        background: "",
        height: $(".canvas").height() - $("#customButtonRow").height(),
        width: $(".canvas").width(),
      };
      secPricesChart.redraw();
    }
  }

  // ===GETTERS===
  get formMetadata() {
    if (!this.metadataSet) return null;
    return this.metadataSet[0].InsaMetaData[0];
  }

  get formMetadataDetails() {
    if (!this.metadataSet) return null;
    return this.metadataSet[0].InsaMetaDataDetails;
  }

  get metadataFormGroups() {
    if (!this.metadataSet) return null;
    return this.metadataSet[0].InsaMetaDataDetails;
  }

  get recordSet() {
    if (!this.metadataSet) return null;
    return this.metadataSet[1].MetaDataSelectedEntity;
  }

  get lookupEntityList() {
    if (!this.metadataSet) return null;
    return this.metadataSet[1].MetaDataLookupEntity;
  }

  get selectedItem() {
    if (!this.selectedId) return null;
    this.alternativeKeyField = null;
    let targetItem = this.recordSet.find((item) => {
      return item[this.keyFields[0]] == this.selectedId;
    });
    if (
      !targetItem &&
      this.metadataName.toLowerCase() === "securityadministration"
    ) {
      targetItem = this.recordSet.find((item) => {
        const isEqual = item.SecNumber == this.selectedId;
        if (isEqual) {
          this.alternativeKeyField = "SecNumber";
        }
        return isEqual;
      });
    }
    return targetItem;
  }

  // METHODS
  deleteItem() {
    if (this.canDelete) {
      const shouldProceed = this.confirmDialog(
        `You are about to delete an item with a ${this.keyFields[0]} of ${this.selectedId}. Do you want to proceed?`,
      );
      if (!shouldProceed) {
        return;
      }
      this.form.disable();
      kendo.ui.progress($(".generic-editor-loading"), true);
      this._insaMetadataService
        .deleteGenericEntity(
          this.selectedItem[this.keyFields[0]],
          this.metadataName,
        )
        .subscribe(
          () => {
            debugger;
            this.showMessage({
              title: "Succeess",
              message: `Item with ${this.keyFields[0]} of ${this.selectedId} Successfully deleted.`,
              onClose: this.onBack,
            });
          },
          () => {
            this.showMessage({
              title: "Error",
              message: `Could not delete an item with an ${this.keyFields[0]} of ${this.selectedId}.`,
              onClose: () => {
                kendo.ui.progress($(".generic-editor-loading"), false);
                this.form.enable();
              },
            });
          },
        );
    }
  }

  setSelfIncrementId(): void {
    if (!this.fields || !this.formMetadataDetails) return;

    const primaryKeyMetadata = this.formMetadataDetails.find(
      (md) => String(md.IsKey).toLowerCase() === "true",
    );

    if (!primaryKeyMetadata) return;

    const primaryKeyFieldName = primaryKeyMetadata.FieldName;

    const field = this.fields.find((f) => f.key === primaryKeyFieldName);
    if (!field) return;

    const control = this.form.controls[field.key];
    if (!control) return;

    if (control.value === null || control.value == 0) {
      control.setValue(this._maxId + 1);
    }
  }

  neutralizeFormValues(): void {
    if (!this.fields || !this.formMetadataDetails) return;

    this.fields.forEach((field) => {
      const control = this.form.controls[field.key];
      if (!control) return;

      const currentValue = control.value;

      if (
        currentValue === null ||
        currentValue === undefined ||
        currentValue == 0
      ) {
        const metadata = this.formMetadataDetails.find(
          (md) => md.FieldName === field.key,
        );
        if (!metadata) return;

        const sqlType = metadata.DataType.toLowerCase();
        let neutralValue: any = null;

        if (!sqlType) {
          neutralValue = "";
        } else if (
          sqlType.includes("char") ||
          sqlType.includes("text") ||
          sqlType.includes("nvarchar") ||
          sqlType.includes("varchar")
        ) {
          neutralValue = "";
        } else if (sqlType.includes("int")) {
          neutralValue = 0;
        } else if (
          sqlType.includes("decimal") ||
          sqlType.includes("numeric") ||
          sqlType.includes("money") ||
          sqlType.includes("float") ||
          sqlType.includes("real")
        ) {
          neutralValue = 0.0;
        } else if (sqlType.includes("bit")) {
          neutralValue = false;
        } else if (
          sqlType.includes("date") ||
          sqlType.includes("time") ||
          sqlType.includes("datetime")
        ) {
          neutralValue = null;
        } else {
          neutralValue = "";
        }

        control.setValue(neutralValue);
      }
    });
  }

  toggleEditorWizard() {
    this.isEditorWizardVisible = !this.isEditorWizardVisible;
  }

  onEditorWizardUpdate(newGroupState: any[]) {
    this.fieldGroups.forEach((fg) => {
      // fg.fields = this.applyFieldOptions(fg.fields, ng.fields)
      const ng = newGroupState.find((ng) => ng.name === fg.name);
      fg.fields = makeSortedFields(
        fg.fields.map((f) => {
          const nf = ng.fields.find((nf) => nf.key === f.key);
          return {
            ...f,
            ...nf,
          };
        }),
      );
    });
    // this.fieldGroups = this.fieldGroups.map(fg => ({ ...fg, fields: makeSortedFields(fg.fields) }));
    this.renderableFieldGroups = this.fieldGroups.map((fg) =>
      makeRenderableFieldGroup(fg),
    );
    // this.saveFieldOptionsToLocalStorage()
  }

  saveFieldOptionsToLocalStorage() {
    const saveableFields = JSON.stringify(
      this.fieldGroups.reduce(
        (acc, fg) => [
          ...acc,
          ...fg.fields.map((fgf) => ({
            key: fgf.key,
            isManuallyHidden: fgf.isManuallyHidden,
            customOrder: fgf.customOrder,
          })),
        ],
        [],
      ),
    );

    localStorage.setItem(
      `genericEditor/${this.metadataName}/fields`,
      saveableFields,
    );
  }

  loadFieldOptionsFromLocalStorage(): PersistedFieldOption[] {
    return JSON.parse(
      localStorage.getItem(`genericEditor/${this.metadataName}/fields`),
    );
  }

  applyFieldOptions(
    fields: CommonField[],
    fieldOptions: PersistedFieldOption[],
  ): CommonField[] {
    if (!fieldOptions) {
      return fields;
    }

    return fields.map((f) => {
      const sf = fieldOptions.find((sf) => sf.key === f.key);
      if (sf) {
        return {
          ...f,
          ...sf,
        };
      } else {
        return f;
      }
    });
  }

  confirmDialog(message) {
    return confirm(message);
  }

  refreshForm(fieldGroups: FieldGroupDefinition[]) {
    this.renderableFieldGroups = undefined;
    this.form = undefined;

    // catch invalid id
    if (this.selectedId && !this.selectedItem) {
      kendo.ui.progress($(".generic-editor-loading"), false);
      this.handleError(
        `No item with ${this.keyFields[0]} of ${this.selectedId}`,
      );
      return;
    }

    this.fields = fieldGroups.reduce((acc, fg) => {
      return [...acc, ...fg.fields];
    }, []);

    this.renderableFieldGroups = fieldGroups.map((fg) =>
      makeRenderableFieldGroup(fg),
    );
    this.form = customFieldsToFormGroup(this.fields);

    if (!this.canEdit) {
      this.form.disable();
    }

    this.title = this.metadataName || "Form title";

    // if (this.selectedItem && this.containsFile()) {
    //   fields.forEach((f: FilePickerField) => {
    //     if (f.file && f.file.currName) {
    //       this.fileContentSubscribers.push(
    //         this._insaMetadataService
    //           .getFile(this.metadataName, f.key, this.selectedId)
    //           .subscribe((data) => {
    //             if (f.file.currName) {
    //               // if the current file wasn't deleted by the user in the meantime
    //               f.file.currContent = data.toString();
    //             }
    //           })
    //       );
    //     }
    //   });
    // }
    kendo.ui.progress($(".generic-editor-loading"), this.form === undefined);
  }

  resetForm() {
    // sets all form control values to their initial values
    if (this.fields) {
      this.fields.forEach((f) => {
        const control = this.form.controls[f.key];
        if (f.file) {
          f.file = {
            initialName: f.file.initialName,
            currName: f.file.initialName,
          };
        } else {
          control.setValue(f.value);
        }
        control.markAsUntouched();
        control.markAsPristine();
      });
    }
  }

  // ENTITY PICKER METHODS
  openEntitySelector({ metadataName, fieldKey }) {
    this.gridSelector = {
      isVisible: true,
      metadataName,
      fieldKey,
    };
  }

  openEntityDetails({ metadataName, id }) {
    this.entityDetails.isVisible = true;
    this.entityDetails.metadataName = metadataName;
    this.entityDetails.id = id;
  }

  gridSelectorSelectHandler(id) {
    this.selectEntity(id, this.gridSelector.fieldKey);
    this.gridSelector.isVisible = false;
    this.gridSelector.metadataSet = null;
  }

  selectEntity(id, fieldKey) {
    this.eventProvider.emit({
      name: "ENTITY_SELECTED",
      value: { id, fieldKey },
    });
    this.gridSelector.isVisible = false;
  }

  closeGridSelector() {
    this.gridSelector.isVisible = false;
  }

  closeEntityDetails() {
    this.entityDetails.isVisible = false;
  }

  containsFile() {
    return this.fields.filter((f) => f.file).length > 0;
  }

  onSubmit() {
    $(".upload-complete").css({ display: "none" });
    $(".progress").css({ display: "block" });
    this.form.disable();

    this.setSelfIncrementId();

    this.neutralizeFormValues();

    const formDTO = convertFormToDTO(this.form, this.fields);

    // actual server request
    const submitObservable = this.selectedId
      ? this._insaMetadataService.updateGenericEntity(
          formDTO,
          this.metadataName,
        )
      : this._insaMetadataService.addGenericEntity(formDTO, this.metadataName);

    submitObservable.subscribe(
      (evt) => {
        if (evt.type === HttpEventType.UploadProgress) {
          const percentProgress = Math.round((evt.loaded / evt.total) * 100);
          $(".progress > .progress-bar").css({
            width: `${percentProgress}%`,
          });
          $(".progress > .progress-bar").text(`${percentProgress}%`);
        } else if (evt.type === HttpEventType.Response) {
          // this._insaMetadataService.getMetaDataInformation(this.metadataName);
          $(".progress").css({ display: "none" });
          $(".form-success").css({ display: "block" });
          this.showMessage({
            title: "Success",
            message: `Entity has been ${
              this.selectedId ? "updated" : "created"
            } sucessfully`,
            onClose: this.onAfterSubmitSuccess || this.onBack,
          });
          this.form.enable();
        }
      },
      (errorMessage) => {
        $(".progress").css({ display: "none" });
        this.form.enable();
        this.renderServerErrors(errorMessage);
        this.handleError();
      },
    );
  }

  showMessage({ title, message, onClose = null }) {
    const modalEl = $("#generic-editor-modal");
    modalEl.find(".modal-title").text(title);
    modalEl.find(".modal-body > p").text(message);
    modalEl.modal({ show: true });
    if (onClose) {
      $("#generic-editor-modal").on("hidden.bs.modal", function (e) {
        onClose();
      });
    }
  }

  renderServerErrors(errorMessage) {
    this.form.setErrors({ error1: errorMessage || "some message" });
  }

  get errorKeys() {
    return Object.keys(this.form.errors);
  }

  handleError(message?) {
    this.showMessage({
      title: "Error",
      message: message || "Something went wrong",
      onClose: this.onBack,
    });
  }
  getSecurityPrices() {
    const selectedValue = this.securityDropdown.value();
    kendo.ui.progress($(".stockChart-loading"), true);
    this._insaService.getSecPricesChart(selectedValue, selectedValue);
  }

  ngOnInit() {
    kendo.ui.progress($(".stockChart-loading"), true);

    this.onResize(null);

    document.getElementById("content").classList.add("flex-override"); // fixes vertical overflow issue

    this.eventProvider.on(
      "OPEN_ENTITY_SELECTOR",
      this.openEntitySelector.bind(this),
    );
    this.eventProvider.on(
      "OPEN_ENTITY_DETAILS",
      this.openEntityDetails.bind(this),
    );

    this._gridStateService.maxPrimaryKeyValue$.subscribe((val) => {
      this._maxId = val;
    });
  }

  ngAfterViewInit() {
    this._localize.applyTranslations(this._element.nativeElement);
    this.waitForKendoDropdown(() => {
      const firstItem = this.securityDropdown.dataSource.at(20);
      const defaultValue = firstItem
        ? firstItem.get(this.securityDropdown.options.dataValueField)
        : null;

      if (defaultValue) {
        this.securityDropdown.value(defaultValue);
        this.getSecurityPrices();
      }

      this.cdr.detectChanges();
      this.initializeChart();
    });
  }
  private waitForKendoDropdown(callback?: () => void): void {
    const checkInterval = 100;
    const maxAttempts = 50;
    let attempts = 0;

    const intervalId = setInterval(() => {
      const dropdownElement = $("#SecurityIndex-kendo");

      if (dropdownElement.length > 0) {
        clearInterval(intervalId);

        this.securityDropdown = dropdownElement.data("kendoDropDownList");

        if (this.securityDropdown) {
          this.securityDropdown.bind(
            "change",
            this.getSecurityPrices.bind(this),
          );
          if (callback) callback();
        }
      } else {
        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(intervalId);
          console.warn("Kendo Dropdown not found after max attempts.");
        }
      }
    }, checkInterval);
  }

  private initializeChart() {
    const container =
      this._element.nativeElement.querySelector("#chart-container");
    if (!container) {
      console.error("Chart container not found");
      return;
    }

    const chartOptions = {
      layout: {
        textColor: "black",
        backgroundColor: "white",
      },
    };

    this.chart = createChart(container, chartOptions);

    const areaSeries = this.chart.addAreaSeries({
      lineColor: "#2962FF",
      topColor: "#2962FF",
      bottomColor: "rgba(41, 98, 255, 0.28)",
    });

    areaSeries.setData([
      { time: "2018-12-22", value: 32.51 },
      { time: "2018-12-23", value: 31.11 },
      { time: "2018-12-24", value: 27.02 },
      { time: "2018-12-25", value: 27.32 },
      { time: "2018-12-26", value: 25.17 },
      { time: "2018-12-27", value: 28.89 },
      { time: "2018-12-28", value: 25.46 },
      { time: "2018-12-29", value: 23.92 },
      { time: "2018-12-30", value: 22.68 },
      { time: "2018-12-31", value: 22.67 },
    ]);

    const candlestickSeries = this.chart.addCandlestickSeries({
      upColor: "#26a69a",
      downColor: "#ef5350",
      borderVisible: false,
      wickUpColor: "#26a69a",
      wickDownColor: "#ef5350",
    });

    candlestickSeries.setData([
      {
        time: "2018-12-22",
        open: 75.16,
        high: 82.84,
        low: 36.16,
        close: 45.72,
      },
      { time: "2018-12-23", open: 45.12, high: 53.9, low: 45.12, close: 48.09 },
      {
        time: "2018-12-24",
        open: 60.71,
        high: 60.71,
        low: 53.39,
        close: 59.29,
      },
      { time: "2018-12-25", open: 68.26, high: 68.26, low: 59.04, close: 60.5 },
      {
        time: "2018-12-26",
        open: 67.71,
        high: 105.85,
        low: 66.67,
        close: 91.04,
      },
      { time: "2018-12-27", open: 91.04, high: 121.4, low: 82.7, close: 111.4 },
      {
        time: "2018-12-28",
        open: 111.51,
        high: 142.83,
        low: 103.34,
        close: 131.25,
      },
      {
        time: "2018-12-29",
        open: 131.33,
        high: 151.17,
        low: 77.68,
        close: 96.43,
      },
      {
        time: "2018-12-30",
        open: 106.33,
        high: 110.2,
        low: 90.39,
        close: 98.1,
      },
      {
        time: "2018-12-31",
        open: 109.87,
        high: 114.69,
        low: 85.66,
        close: 111.26,
      },
    ]);

    this.chart.timeScale().fitContent();
  }

  ngOnDestroy() {
    document.getElementById("content").classList.remove("flex-override"); // fixes vertical overflow issue

    if (this.metaDataSubscription) {
      this.metaDataSubscription.unsubscribe();
    }
    this.metadataSet = null;
    this.secPricesSubscription.unsubscribe();
    if (this.chart) {
      this.chart.remove();
    }
  }

  ngOnChanges() {
    if (this.metadataName) {
      kendo.ui.progress($(".generic-editor-loading"), true);
      const fieldOptions = this.loadFieldOptionsFromLocalStorage();
      this._insaMetadataService
        .getMetaDataInformation(this.metadataName)
        .subscribe(
          (metadataSet: MetadataSet) => {
            this.metadataSet = makeParsedMetadataSet(
              metadataSet,
              this._localize.getLocalizedResource.bind(this._localize),
            );
            this.canDelete = this.canDelete
              ? this.metadataSet[0].InsaMetaData[0].AllowDelete
              : false;
            this.canEdit = this.canEdit
              ? this.metadataSet[0].InsaMetaData[0].AllowAddNew
              : false;
            this.keyFields = computeKeyFields(this.formMetadataDetails);
            if (!this.keyFields.length) {
              console.warn(`${this.metadataName} metadata has no key field`);
            }
            // this.metadataName = this.metadataSet[0].InsaMetaData[0].MetaDataName;
            const customFields = InsaFieldsToCustomFields(
              this.formMetadataDetails,
              this.lookupEntityList,
              this.selectedItem,
            );

            const combinedFields = this.applyFieldOptions(
              customFields,
              fieldOptions,
            );

            this.fieldGroups = makeParsedMetadataGroups(
              metadataSet[0].InsaMetadataFormGroups,
              metadataSet[0].InsaMetaData[0],
              combinedFields,
            ).map((parsedG) =>
              makeFieldGroup(parsedG, {
                localize: this._localize.getLocalizedResource,
              }),
            );
            this.refreshForm(this.fieldGroups);
          },
          (err) => {
            this.handleError(err);
          },
        );
    } else if (this.fieldGroups) {
      this.refreshForm(this.fieldGroups);
    } else {
      console.error(
        "GenericEditorComponent requires either `groups`, or `metadataName` input property. It didn't get any.",
      );
    }

    this.fileContentSubscribers.forEach((sub) => {
      sub.unsubscribe();
    });
    this.fileContentSubscribers = [];
    // this.refreshForm();
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-generic-form/generic-editor/generic-editor.component.ts